/* ============================================================
   docSearch — full-text search across every reviewed document

   Everything lives locally (IndexedDB), so the "index" is just each doc
   flattened into searchable fields once: its name, its text (markdown
   syntax stripped so snippets read like prose), and everything said about
   it — comment notes, the quoted passages, replies, and the handoff
   summary + general notes.

   A query is whitespace-separated terms, case-insensitive; a doc matches
   when every term appears somewhere in it (not necessarily in the same
   field). Results come back ranked: name matches first, then by number
   of hits, then in the incoming (most-recent-first) order.
   ============================================================ */

import type { Tab } from '@/types'

export type FieldKind = 'name' | 'text' | 'comments'

interface Field {
  kind: FieldKind
  text: string
  lower: string
}

export interface IndexedDoc {
  doc: Tab
  fields: Field[]
}

/** A run of snippet text; `hit` runs are the matched terms. */
export interface SnippetPart {
  text: string
  hit: boolean
}

export interface SearchResult {
  doc: Tab
  /** Occurrences of all terms, per field kind. */
  hits: Record<FieldKind, number>
  /** Where the snippet came from (never 'name' — the row already shows it). */
  snippetKind: Exclude<FieldKind, 'name'> | null
  snippet: SnippetPart[]
}

const SNIPPET_RADIUS = 60

/** Markdown → readable plain text (good enough for matching + snippets). */
export function plainText(markdown: string): string {
  return markdown
    .replace(/^```.*$/gm, '')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}(#{1,6}|>+|[-*+]|\d+[.)])\s+/gm, '')
    .replace(/^\s*\|?[-:| ]+\|[-:| ]*$/gm, '')
    .replace(/[*_`~|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function field(kind: FieldKind, text: string): Field {
  const lower = text.toLowerCase()
  // Case-folding can change length for a few characters (e.g. 'İ'); fall
  // back to showing the folded text so snippet offsets always line up.
  return { kind, text: lower.length === text.length ? text : lower, lower }
}

export function indexDoc(doc: Tab): IndexedDoc {
  const said: string[] = []
  for (const c of doc.comments || []) {
    said.push(c.note, c.quotedText)
    for (const r of c.replies || []) said.push(r.note)
  }
  said.push(doc.summary || '', doc.generalNotes || '')
  return {
    doc,
    fields: [
      field('name', `${doc.filename} ${doc.shortHash}`),
      field('text', plainText(doc.markdown || '')),
      field('comments', said.filter((s) => s && s.trim()).join(' · ').replace(/\s+/g, ' ')),
    ],
  }
}

export function queryTerms(query: string): string[] {
  const terms = [...new Set(query.toLowerCase().split(/\s+/).filter(Boolean))]
  // A longer term containing a shorter one would double-count and overlap.
  return terms.filter((t) => !terms.some((o) => o !== t && o.includes(t)))
}

function count(haystack: string, term: string): number {
  let n = 0
  for (let i = haystack.indexOf(term); i !== -1; i = haystack.indexOf(term, i + term.length)) n++
  return n
}

function snippetAround(f: Field, terms: string[]): SnippetPart[] {
  let first = Infinity
  for (const t of terms) {
    const i = f.lower.indexOf(t)
    if (i !== -1 && i < first) first = i
  }
  if (first === Infinity) return []
  let start = Math.max(0, first - SNIPPET_RADIUS)
  let end = Math.min(f.text.length, first + SNIPPET_RADIUS * 2)
  // Snap to word boundaries so the snippet doesn't open mid-word.
  if (start > 0) {
    const space = f.text.indexOf(' ', start)
    if (space !== -1 && space < first) start = space + 1
  }
  if (end < f.text.length) {
    const space = f.text.lastIndexOf(' ', end)
    if (space > first) end = space
  }

  // Mark every term occurrence inside the window.
  const marks: Array<[number, number]> = []
  const lower = f.lower.slice(start, end)
  for (const t of terms) {
    for (let i = lower.indexOf(t); i !== -1; i = lower.indexOf(t, i + t.length)) marks.push([i, i + t.length])
  }
  marks.sort((a, b) => a[0] - b[0])

  const text = f.text.slice(start, end)
  const parts: SnippetPart[] = []
  let at = 0
  for (const [s, e] of marks) {
    if (s < at) continue
    if (s > at) parts.push({ text: text.slice(at, s), hit: false })
    parts.push({ text: text.slice(s, e), hit: true })
    at = e
  }
  if (at < text.length) parts.push({ text: text.slice(at), hit: false })
  if (start > 0) parts.unshift({ text: '…', hit: false })
  if (end < f.text.length) parts.push({ text: '…', hit: false })
  return parts
}

export function searchDocs(index: readonly IndexedDoc[], query: string): SearchResult[] {
  const terms = queryTerms(query)
  if (!terms.length) return []

  const ranked: Array<SearchResult & { order: number; total: number }> = []
  index.forEach((entry, order) => {
    if (!terms.every((t) => entry.fields.some((f) => f.lower.includes(t)))) return
    const hits: Record<FieldKind, number> = { name: 0, text: 0, comments: 0 }
    for (const f of entry.fields) for (const t of terms) hits[f.kind] += count(f.lower, t)

    // Snippet from the body when it matches, else from the comments.
    const source = entry.fields.find((f) => f.kind === 'text' && hits.text > 0) ??
      entry.fields.find((f) => f.kind === 'comments' && hits.comments > 0)
    ranked.push({
      doc: entry.doc,
      hits,
      snippetKind: source ? (source.kind as SearchResult['snippetKind']) : null,
      snippet: source ? snippetAround(source, terms) : [],
      order,
      total: hits.name + hits.text + hits.comments,
    })
  })

  ranked.sort(
    (a, b) => Number(b.hits.name > 0) - Number(a.hits.name > 0) || b.total - a.total || a.order - b.order,
  )
  return ranked.map(({ order: _o, total: _t, ...r }) => r)
}
